import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AlertController,
  IonicModule,
  LoadingController,
  ModalController,
  NavController,
  PopoverController,
} from '@ionic/angular';
import _ from 'lodash';
import Markdown from 'markdown-it';
import moment from 'moment-timezone';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { v4 as uuid } from 'uuid';

import { AutomationLogsComponent } from 'src/app/components/automation-logs/automation-logs.component';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { CustomFieldsModule } from 'src/app/components/custom-fields/custom-fields.module';
import { EmojiPickerComponent } from 'src/app/components/emoji-picker/emoji-picker.component';
import { TimePickerComponent } from 'src/app/components/time-picker/time-picker.component';
import {
  AUTOMATION_ACTION_TYPES_ICONS,
  AUTOMATION_ACTION_TYPES_LABELS,
  CONVERSATION_PRIORITY_VALUES,
  CONVERSATION_STATUS,
  PROJECT_MEMBER_ROLES,
} from 'src/app/constants';
import { extractTicketId } from 'src/app/utils/custom-fields';
import { getAmPmFromMilitaryTime, getMilitaryTime } from 'src/app/utils/utils';
import { CustomFieldsService } from 'src/services/custom-fields.service';

import {
  Automation,
  AUTOMATION_ACTION_TYPES,
  AUTOMATION_STEP_TYPES,
  AUTOMATION_TRIGGER_TYPES,
  AutomationRun,
  AutomationStep,
  AutomationTrigger,
  CONDITIONS_TYPES,
  CUSTOM_FIELD_TYPES,
  CustomField,
  ManualAutomationTrigger,
  SIMPLE_CONDITIONS_OPERATORS,
  SimpleConditions,
  SimpleConditionsLeaf,
  SimpleConditionsOperator,
  SimpleConditionsWrapper,
  SLACK_TEMPLATE_BODY_TYPE,
  SLACK_TEMPLATE_SEND_AS_OPTIONS,
  SlackReactionAutomationTrigger,
} from '../../../models';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { AutomationAssistantMessage, AutomationAssistantService } from '../../../services/automation-assistant.service';
import { AutomationsService } from '../../../services/automations.service';
import { MessageService } from '../../../services/message.service';
import { SearchablePopoverService } from '../../../services/searchable-popover.service';
import {
  ActionDropdownComponent,
  ActionDropdownType,
} from '../../components/action-dropdown/action-dropdown.component';
import { FilterButtonComponent } from '../../components/filter-button/filter-button.component';
import {
  FILTER_OPERATORS,
  FilterOption,
  reconstructAndHydrateUiFilters,
  SelectedFilterMap,
} from '../../utils/filter-utils';
import { AutomationActionStepComponent } from '../automation-action-step/automation-action-step.component';

const AUTOMATION_TRIGGER_OBJECT_TYPES = {
  CONVERSATION: 'conversation',
} as const;

const AUTOMATION_TRIGGER_EVENTS = {
  [AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION]: {
    CREATED: 'created',
    UPDATED: 'updated',
    APPROVAL_REQUEST_APPROVED: 'approvalRequestApproved',
    APPROVAL_REQUEST_REJECTED: 'approvalRequestRejected',
    APPROVAL_REQUEST_CREATED: 'approvalRequestCreated',
    MESSAGE_ADDED: 'messageAdded',
    INTERNAL_MESSAGE_ADDED: 'internalMessageAdded',
    EXTERNAL_MESSAGE_ADDED: 'externalMessageAdded',
    CLOSED: 'closed',
    REOPENED: 'reopened',
    PUT_ON_HOLD: 'putOnHold',
    INTERNAL_UNRESPONSIVE: 'internalUnresponsive',
    EXTERNAL_UNRESPONSIVE: 'externalUnresponsive',
    TRIAGED: 'triaged',
    TRIAGE_MESSAGE_ADDED: 'triageMessageAdded',
    ESCALATION_CREATED: 'escalationCreated',
    ESCALATION_ACKNOWLEDGED: 'escalationAcknowledged',
    ESCALATION_RESOLVED: 'escalationResolved',
  },
} as const;

const CRON_OPTIONS = {
  DAY: 'day',
  WEEK: 'week',
} as const;
const CRON_DAYS_OPTIONS = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
} as const;

@Component({
  selector: 'app-edit-automation',
  templateUrl: './edit-automation.component.html',
  styleUrls: ['./edit-automation.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    ChannelSelectorModule,
    CustomFieldsModule,
    FilterButtonComponent,
    NgxTippyModule,
    AutomationActionStepComponent,
  ],
  animations: [
    trigger('listAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.9) translateY(-20px)', filter: 'blur(8px)' }),
        animate(
          '400ms cubic-bezier(0.25, 1, 0.5, 1)',
          style({ opacity: 1, transform: 'scale(1) translateY(0)', filter: 'blur(0px)' }),
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, filter: 'blur(0px)' }),
        animate('400ms cubic-bezier(0.25, 1, 0.5, 1)', style({ opacity: 0, filter: 'blur(8px)' })),
      ]),
    ]),
  ],
})
export class EditAutomationComponent implements OnInit {
  @ViewChild('content') private content: any;
  @ViewChild('nameInput', { static: false }) nameInput;
  @ViewChild('filterButton', { static: false }) filterButton: FilterButtonComponent;
  @ViewChild('assistantInputElement', { static: false }) assistantInputElement;

  allCustomFieldsMap: Map<string, CustomField> = new Map();

  AUTOMATION_ACTION_TYPES = AUTOMATION_ACTION_TYPES;
  AUTOMATION_TRIGGER_TYPES = AUTOMATION_TRIGGER_TYPES;
  AUTOMATION_TRIGGER_OBJECT_TYPES = AUTOMATION_TRIGGER_OBJECT_TYPES;
  AUTOMATION_TRIGGER_EVENTS = AUTOMATION_TRIGGER_EVENTS;
  AUTOMATION_STEP_TYPES = AUTOMATION_STEP_TYPES;

  projectId: string | null = null;

  CRON_FREQUENCIES = [
    {
      label: 'Daily',
      value: CRON_OPTIONS.DAY,
    },
    {
      label: 'Weekly',
      value: CRON_OPTIONS.WEEK,
    },
  ];
  CRON_DAYS = Object.keys(CRON_DAYS_OPTIONS);
  cronFrequency: string;
  cronDay: string;
  cronTime: string;
  cronTimeDisplay: string;

  isLoading = false;
  isLoadingRuns = false;
  isInitializing = true;
  isActive = true;
  name = 'New Automation';
  description: string;
  id?: string;
  updatedAt?: Date;
  functionsVersions: any[];
  functionNames: string[];
  inputFields: any[] = [];

  trigger: AutomationTrigger = {
    type: AUTOMATION_TRIGGER_TYPES.EVENT,
    data: {
      event: null,
      objectType: 'conversation',
    },
  };

  eventTriggerConfig: Record<string, any> = {};

  steps: AutomationStep[] = [];

  automationRuns: Partial<AutomationRun>[] = [];

  selectedTab: 'build' | 'test' | 'runs' | 'assistant' = 'build';

  actionsFilterType: 'all' | 'actions' | 'logic' | 'apps' = 'all';

  testObjectId: string;
  testRunId: string;
  testIsRunning: boolean;
  testLogs: any[];

  // Assistant chat properties
  assistantMessages: AutomationAssistantMessage[] = [];
  assistantInput: string = '';
  isAssistantThinking: boolean = false;

  // Markdown renderer for assistant messages
  markdown = new Markdown({
    breaks: true, // Converts line breaks to <br> tags
    linkify: true, // Automatically converts URLs to clickable links
  });

  CONVERSATION_UPDATE_SUB_EVENTS = [
    {
      label: 'Status',
      value: 'subStatusChanged',
    },
    {
      label: 'Status Type',
      value: 'statusChanged',
    },
    {
      label: 'Assignee',
      value: 'assigneeChanged',
    },
    {
      label: 'Tags',
      value: 'tagsChanged',
    },
  ];

  triggerConfigFields = {
    'event:conversation:updated': {
      subEvent: true,
    },
    'event:conversation:internalUnresponsive': {
      workingTimeOnly: true,
      minutes: true,
    },
    'event:conversation:externalUnresponsive': {
      workingTimeOnly: true,
      minutes: true,
    },
  };

  filterOptions: FilterOption[] = [
    {
      label: 'Project',
      id: 'projectId',
      icon: 'custom-project',
      type: 'select',
    },
    {
      label: 'Assignee',
      id: 'assigneeId',
      searchId: 'assignedToUserId',
      icon: 'person',
      type: 'select',
    },
    !this.authService.tenant.accountsEnabled
      ? {
          label: 'Customer',
          id: 'customerId',
          icon: 'business',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Channel',
          id: 'customerId',
          icon: 'chatbubbles',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account',
          id: 'accountId',
          icon: 'business',
          type: 'select',
        }
      : undefined,
    // {
    //   label: 'Customer tag',
    //   id: 'customer.tags.id',
    //   icon: 'pricetag',
    //   type: 'select',
    // },
    {
      label: 'Ticket Type',
      id: 'ticketTypeId',
      icon: 'ticket',
      type: 'select',
    },
    {
      label: 'Custom Field',
      id: 'customFieldId',
      type: 'variable',
      icon: 'flask',
      searchablePopoverOptions: {
        allowedTypes: [
          CUSTOM_FIELD_TYPES.CHECKBOX,
          CUSTOM_FIELD_TYPES.SINGLE_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_SELECT,
          CUSTOM_FIELD_TYPES.DATE,
          CUSTOM_FIELD_TYPES.USER_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_USER_SELECT,
          CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT,
        ],
      },
    },
    {
      label: 'Tag',
      id: 'tagId',
      searchId: 'tags.id',
      icon: 'pricetag',
      type: 'select',
    },
    !this.authService.tenant.accountsEnabled
      ? {
          label: 'Customer Tag',
          id: 'customerTagId',
          searchId: 'customer.tags.id',
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Channel Tag',
          id: 'customerTagId',
          searchId: 'customer.tags.id',
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account Tag',
          id: 'accountTagId',
          searchId: 'account.tags.id',
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
    {
      label: 'Source',
      id: 'sourceType',
      icon: 'log-in',

      type: 'select',
      values: [
        {
          label: 'Slack',
          id: 'slack',
        },
        {
          label: 'Email',
          id: 'email',
        },
        {
          label: 'In-app',
          id: 'widget',
        },
        {
          label: 'Dashboard',
          id: 'dashboard',
        },
        {
          label: 'Microsoft Teams',
          id: 'microsoft-teams',
        },
        {
          label: 'Automation',
          id: 'automation',
        },
      ],
    },
    {
      label: 'Status Type',
      id: 'status',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: 'Status',
      id: 'subStatus.key',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: 'Priority',
      id: 'priority',
      icon: 'alert-circle',
      type: 'select',
    },
    {
      label: 'Is New?',
      id: 'isNew',
      icon: 'pulse',
      type: 'select',
      values: [
        {
          label: 'Yes',
          id: true,
        },
        {
          label: 'No',
          id: false,
        },
      ],
    },
    {
      label: 'Is During Working Hours?',
      id: 'isWorkingTime',
      icon: 'time',
      type: 'select',
      values: [
        {
          label: 'Yes',
          id: true,
        },
        {
          label: 'No',
          id: false,
        },
      ],
    },
    {
      label: 'Sentiment',
      id: 'sentimentNormalized',
      icon: 'happy',
      type: 'select',
      values: [
        {
          label: 'Very Positive',
          id: 'veryPositive',
        },
        {
          label: 'Positive',
          id: 'positive',
        },
        {
          label: 'Neutral',
          id: 'neutral',
        },
        {
          label: 'Negative',
          id: 'negative',
        },
        {
          label: 'Very Negative',
          id: 'veryNegative',
        },
      ],
    },
    {
      label: 'Is Autoresponse',
      id: 'isAutoresponse',
      icon: 'chatbox-ellipses',
      type: 'select',
      values: [
        {
          label: 'Yes',
          id: true,
        },
        {
          label: 'No',
          id: false,
        },
      ],
    },
  ].filter(Boolean) as FilterOption[];
  filters: SelectedFilterMap = new Map();

  editorOptions = { theme: 'vs-dark', language: 'typescript', automaticLayout: true };

  shouldShowDismissConfirmation = false;

  lookups: Record<string, Record<string, string>> = {};

  actionOptions: ActionDropdownType[] = [
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.TRIGGER_FUNCTION],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.TRIGGER_FUNCTION],
      metadata: {
        description: 'Run a custom function written in TypeScript',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        category: 'actions',
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.TRIGGER_FUNCTION,
          data: {
            code: 'export default async function main({ input, unthreadClient, state, secrets }) {\n\t// code here\n}',
            functionName: '',
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.CREATE_APPROVAL_REQUEST],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.CREATE_APPROVAL_REQUEST],
      metadata: {
        description: 'Send an approval request to a user or group',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        category: 'actions',
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.CREATE_APPROVAL_REQUEST,
          data: {
            entityId: '',
            entityType: '',
            title: '',
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.ASSIGN_CONVERSATION],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.ASSIGN_CONVERSATION],
      metadata: {
        category: 'actions',
        description: 'Set and notify the assignee of the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.ASSIGN_CONVERSATION,
          data: {
            entityId: '',
            entityType: '',
            skipNextWaitIfNoAssignee: false,
            isEscalation: false,
            cycleThroughTeamMembers: {
              enabled: false,
            },
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.ADD_CONVERSATION_FOLLOWER],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.ADD_CONVERSATION_FOLLOWER],
      metadata: {
        category: 'actions',
        description: 'Add a follower to the conversation to get notified when it changes',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.ADD_CONVERSATION_FOLLOWER,
          data: {
            entityId: '',
            entityType: '',
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.SET_CONVERSATION_COLLABORATOR],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.SET_CONVERSATION_COLLABORATOR],
      metadata: {
        category: 'actions',
        description: 'Set a collaborator for the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.SET_CONVERSATION_COLLABORATOR,
          data: {
            collaboratorTypeId: '',
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.SET_CONVERSATION_PRIORITY],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.SET_CONVERSATION_PRIORITY],
      metadata: {
        category: 'actions',
        description: 'Set the priority level of the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.SET_CONVERSATION_PRIORITY,
          data: {
            priority: CONVERSATION_PRIORITY_VALUES.HIGH,
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.CHANGE_CONVERSATION_STATUS],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.CHANGE_CONVERSATION_STATUS],
      metadata: {
        category: 'actions',
        description: 'Set the status type of the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.CHANGE_CONVERSATION_STATUS,
          data: {
            status: CONVERSATION_STATUS.OPEN,
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.CHANGE_CONVERSATION_SUB_STATUS],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.CHANGE_CONVERSATION_SUB_STATUS],
      metadata: {
        category: 'actions',
        description: 'Set the status of the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.CHANGE_CONVERSATION_SUB_STATUS,
          data: {
            subStatusKey: '',
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.SET_CONVERSATION_TICKET_TYPE],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.SET_CONVERSATION_TICKET_TYPE],
      metadata: {
        category: 'actions',
        description: 'Set the ticket type of the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.SET_CONVERSATION_TICKET_TYPE,
          data: {
            ticketTypeId: '',
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD],
      metadata: {
        category: 'actions',
        description: 'Set a custom field of the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD,
          data: {
            customFieldId: '',
            value: '',
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.ADD_CONVERSATION_TAGS],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.ADD_CONVERSATION_TAGS],
      metadata: {
        category: 'actions',
        description: 'Add one or more tags to the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.ADD_CONVERSATION_TAGS,
          data: {
            tagIds: [],
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.SEND_CONVERSATION_REMINDER],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.SEND_CONVERSATION_REMINDER],
      metadata: {
        category: 'actions',
        description: 'Send a Slack reminder to the conversation assignee',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.SEND_CONVERSATION_REMINDER,
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.SEND_SLACK_TEMPLATE],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.SEND_SLACK_TEMPLATE],
      metadata: {
        category: 'actions',
        description: 'Send a message in the main thread of the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.SEND_SLACK_TEMPLATE,
          data: {
            templateId: '',
            sendAs: SLACK_TEMPLATE_SEND_AS_OPTIONS.SUPPORT_TEAM,
            body: {
              type: SLACK_TEMPLATE_BODY_TYPE.HTML,
              value: '',
            },
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.SET_SLACK_USER_GROUP_MEMBERSHIP],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.SET_SLACK_USER_GROUP_MEMBERSHIP],
      metadata: {
        category: 'actions',
        description: 'Set the users of a Slack User Group',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.SET_SLACK_USER_GROUP_MEMBERSHIP,
          data: {
            slackUserGroupId: '',
            userIds: [],
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.POST_TO_TRIAGE],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.POST_TO_TRIAGE],
      metadata: {
        category: 'actions',
        description: 'Cross-post conversation to a triage channel',
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.POST_TO_TRIAGE,
          data: {
            channelId: '',
            templateId: '',
            note: '',
            tag: {
              entityId: '',
              entityType: 'user',
            },
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.SUMMARIZE_CONVERSATION],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.SUMMARIZE_CONVERSATION],
      metadata: {
        category: 'actions',
        description: 'Create a summary of the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.SUMMARIZE_CONVERSATION,
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.LOCK_CONVERSATION],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.LOCK_CONVERSATION],
      metadata: {
        category: 'actions',
        description: 'Prevent customers from re-opening the conversation',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.LOCK_CONVERSATION,
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.GENERATE_AI_MESSAGE],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.GENERATE_AI_MESSAGE],
      metadata: {
        category: 'actions',
        description: 'Generate AI message for use in other steps',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.GENERATE_AI_MESSAGE,
          data: {},
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.WAIT],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.WAIT],
      metadata: {
        category: 'logic',
        description: 'Wait for a specified amount of time before continuing',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.WAIT,
          data: {
            wait: {
              minutes: 60,
              workingTimeOnly: false,
              abortOn: [],
            },
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.ADD_USER_TO_GOOGLE_GROUP],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.ADD_USER_TO_GOOGLE_GROUP],
      metadata: {
        category: 'apps',
        description: 'Add a user to a Google Group',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.ADD_USER_TO_GOOGLE_GROUP,
          data: {
            groupId: '',
            userId: '',
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.ADD_REACTION_EMOJI],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.ADD_REACTION_EMOJI],
      metadata: {
        category: 'actions',
        description: 'Add a reaction emoji to the top message of the conversation thread',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.ADD_REACTION_EMOJI,
          data: {
            emoji: '',
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION],
      metadata: {
        category: 'actions',
        description: 'Create a new conversation with configurable properties',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION,
          data: {
            title: '',
            description: undefined,
            projectId: undefined,
            assignedToUserId: undefined,
            priority: undefined,
            status: undefined,
            customerId: undefined,
            accountId: undefined,
            submitterId: undefined,
            ticketTypeId: undefined,
            customFieldValues: undefined,
            tagIds: undefined,
            conversationLinks: undefined,
          },
        },
      },
    },
    {
      label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.CREATE_ESCALATION],
      icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.CREATE_ESCALATION],
      metadata: {
        category: 'actions',
        description: 'Create a new escalation with configurable properties',
        stepType: AUTOMATION_STEP_TYPES.ACTION,
        action: {
          id: uuid(),
          type: AUTOMATION_ACTION_TYPES.CREATE_ESCALATION,
          data: {
            escalationTypeId: '',
            title: undefined,
            body: '',
            publicNote: '',
            priority: undefined,
            assignedToGroupId: undefined,
            assignedToUserId: undefined,
            createdBy: undefined,
          },
        },
      },
    },
    this.authService.tenant.integrations?.pagerDuty?.enabled
      ? {
          label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.CREATE_PAGERDUTY_PAGE],
          icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.CREATE_PAGERDUTY_PAGE],
          metadata: {
            category: 'apps',
            description: 'Page on-call for a PagerDuty service',
            stepType: AUTOMATION_STEP_TYPES.ACTION,
            action: {
              id: uuid(),
              type: AUTOMATION_ACTION_TYPES.CREATE_PAGERDUTY_PAGE,
              data: {
                serviceId: '',
                title: '',
                incidentDetails: '',
                requesterUserId: undefined,
              },
            },
          },
        }
      : null,
    {
      label: 'Branching Step',
      icon: 'git-branch',
      metadata: {
        category: 'logic',
        description: 'Add a condition-based branching step with multiple sub-steps in each branch',
        stepType: AUTOMATION_STEP_TYPES.BRANCHES,
        branches: [
          {
            conditions: {
              type: CONDITIONS_TYPES.SIMPLE,
              simple: {},
            },
            steps: [],
            filters: new Map(),
          },
          {
            conditions: {
              type: CONDITIONS_TYPES.SIMPLE,
              simple: {},
            },
            steps: [],
            filters: new Map(),
          },
        ],
      },
    },
  ].filter((action) => action != null);

  draggingOverStepIndex: number | null = null;
  draggingOverBranch: any;
  animationStep: any;

  timezoneOptions = moment.tz.names();
  timezone: string = moment.tz.guess();

  searchTerm: string = '';

  /**
   * Check if the automation assistant feature is enabled
   */
  get isAutomationAssistantEnabled(): boolean {
    return this.automationAssistantService.isFeatureEnabled();
  }

  constructor(
    public route: ActivatedRoute,
    public authService: AuthService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private msgSrvc: MessageService,
    private navCtrl: NavController,
    private loadingCtrl: LoadingController,
    private automationsService: AutomationsService,
    private searchablePopoverService: SearchablePopoverService,
    private automationAssistantService: AutomationAssistantService,
    private apiService: ApiService,
    private customFieldsService: CustomFieldsService,
  ) {
    this.id = route.snapshot.paramMap.get('automationId');
    if (this.id === 'new') {
      this.id = undefined;
    }
  }

  async ngOnInit() {
    try {
      const customFieldsResponse = await this.customFieldsService.list({ limit: null });
      // Ensure the data maps correctly
      this.allCustomFieldsMap = new Map(customFieldsResponse.data.map((field: CustomField) => [field.id, field]));
    } catch (error) {
      console.error('Failed to fetch all custom fields:', error);
      await this.msgSrvc.showError('Could not load custom field definitions.');
      return; // Prevent further execution if fields fail to load
    }

    if (this.id) {
      await this.loadAutomation();
    } else {
      this.isInitializing = false;
      this.projectId = this.authService.selectedProject?.id ?? null;
    }

    // Redirect to build tab if assistant tab is selected but feature is disabled
    if (this.selectedTab === 'assistant' && !this.isAutomationAssistantEnabled) {
      this.selectedTab = 'build';
    }

    // Add integration-specific actions
    if (this.authService.tenant?.externalTaskManagementConfig?.type === 'jira') {
      this.actionOptions.push({
        label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.CREATE_JIRA_TASK],
        icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.CREATE_JIRA_TASK],
        metadata: {
          category: 'apps',
          description: 'Create an issue (story, task, bug, etc.) in Jira with conversation details',
          stepType: AUTOMATION_STEP_TYPES.ACTION,
          action: {
            id: uuid(),
            type: AUTOMATION_ACTION_TYPES.CREATE_JIRA_TASK,
            data: {
              projectKey: '',
              issueType: '',
              summary: '',
              description: '',
              priority: '',
              priorityMapping: {},
            },
          },
        },
      });
    }

    // Add Okta integration actions if tenant has Okta configured
    if (this.authService.tenant?.integrations?.okta?.enabled) {
      this.actionOptions.push({
        label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.GRANT_OKTA_GROUP_ACCESS],
        icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.GRANT_OKTA_GROUP_ACCESS],
        metadata: {
          category: 'apps',
          description: 'Grant access to an Okta group for a user',
          stepType: AUTOMATION_STEP_TYPES.ACTION,
          action: {
            id: uuid(),
            type: AUTOMATION_ACTION_TYPES.GRANT_OKTA_GROUP_ACCESS,
            data: {
              oktaGroupId: '',
              userId: '',
              expiresIn: undefined,
            },
          },
        },
      });

      this.actionOptions.push({
        label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.REVOKE_OKTA_GROUP_ACCESS],
        icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.REVOKE_OKTA_GROUP_ACCESS],
        metadata: {
          category: 'apps',
          description: 'Revoke access to an Okta group from a user',
          stepType: AUTOMATION_STEP_TYPES.ACTION,
          action: {
            id: uuid(),
            type: AUTOMATION_ACTION_TYPES.REVOKE_OKTA_GROUP_ACCESS,
            data: {
              oktaGroupId: '',
              userId: '',
            },
          },
        },
      });

      this.actionOptions.push({
        label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.ASSIGN_OKTA_APPLICATION_ACCESS],
        icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.ASSIGN_OKTA_APPLICATION_ACCESS],
        metadata: {
          category: 'apps',
          description: 'Assign user access to an Okta application',
          stepType: AUTOMATION_STEP_TYPES.ACTION,
          action: {
            id: uuid(),
            type: AUTOMATION_ACTION_TYPES.ASSIGN_OKTA_APPLICATION_ACCESS,
            data: {
              oktaApplicationId: '',
              userId: '',
              expiresIn: undefined,
            },
          },
        },
      });

      this.actionOptions.push({
        label: AUTOMATION_ACTION_TYPES_LABELS[AUTOMATION_ACTION_TYPES.REVOKE_OKTA_APPLICATION_ACCESS],
        icon: AUTOMATION_ACTION_TYPES_ICONS[AUTOMATION_ACTION_TYPES.REVOKE_OKTA_APPLICATION_ACCESS],
        metadata: {
          category: 'apps',
          description: 'Revoke user access from an Okta application',
          stepType: AUTOMATION_STEP_TYPES.ACTION,
          action: {
            id: uuid(),
            type: AUTOMATION_ACTION_TYPES.REVOKE_OKTA_APPLICATION_ACCESS,
            data: {
              oktaApplicationId: '',
              userId: '',
            },
          },
        },
      });
    }

    // Fetch functions versions *after* custom fields potentially needed for lookups
    this.getFunctionsVersions();

    this.route.queryParams.subscribe(async (params) => {
      if (params.tab === 'assistant') {
        this.selectedTab = 'assistant';
        setTimeout(() => {
          this.assistantInputElement.setFocus();
        }, 100);
      } else if (params.tab === 'test') {
        this.selectedTab = 'test';
      } else {
        this.selectedTab = 'build';
      }
    });
  }

  /**
   * Load Automation Flow:
   *
   * 1. loadAutomation()
   *    ├─ Fetches automation data from server
   *    ├─ Sets basic properties (name, description, steps, trigger)
   *    ├─ Processes trigger-specific configurations:
   *    │  ├─ Scheduled: Parses cron expression and timezone
   *    │  ├─ Manual: Sets input fields
   *    │  └─ Event: Sets trigger config
   *    │
   *    ├─ Builds lookup IDs collection:
   *    │  ├─ conditionsToFilterMap() - Processes main automation conditions
   *    │  ├─ collectLookupIdsFromSteps() - Recursively collects IDs from all steps
   *    │  └─ Processes branch conditions
   *    │
   *    ├─ Fetches lookup values from API:
   *    │  ├─ Gets entity labels (users, ticket types, etc.)
   *    │  └─ Adds static lookups (isNew, isWorkingTime)
   *    │
   *    └─ Sets labels for all filters:
   *       ├─ setLabelsForFilters() on main filters
   *       └─ setLabelsForFilters() on each branch's filters
   *
   * Key Methods:
   * - conditionsToFilterMap: Converts DB conditions to UI filter format
   * - collectLookupIdsFromSteps: Gathers all referenced entity IDs
   * - setLabelsForFilters: Hydrates filters with human-readable labels
   *
   * This process ensures all entity references (users, ticket types, etc.)
   * are properly resolved to their display values for the UI.
   */
  async loadAutomation() {
    this.isInitializing = true;

    try {
      const automation = await this.automationsService.getById(this.id);
      await this.populateAutomationData(automation);
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Loads automation from JSON object without API call for LLM-generated automations
   */
  async loadAutomationFromJSON(automation: Automation) {
    try {
      await this.populateAutomationData(automation);
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  /**
   * Shared logic for populating component state from automation object
   * Used by both loadAutomation() and loadAutomationFromJSON()
   */
  private async populateAutomationData(automation: Automation) {
    this.projectId = automation.projectId;
    this.name = automation.name;
    this.description = automation.description;
    this.steps = automation.steps;
    this.trigger = automation.trigger;
    this.updatedAt = automation.updatedAt;
    this.isActive = automation.status === 'active';

    // We break out cron fields into their own properties
    if (this.trigger.type === AUTOMATION_TRIGGER_TYPES.SCHEDULED) {
      const components = this.trigger.data.cron?.split(' ');

      this.timezone = this.trigger.data.timezone ?? moment.tz.guess();

      const [minute, hour, _dayOfMonth, _month, dayOfWeek] = components;
      this.cronTime = `${hour}:${minute.length < 2 ? '0' + minute : minute}`;
      this.cronTimeDisplay = getAmPmFromMilitaryTime(this.cronTime);
      this.cronDay = dayOfWeek === '*' ? null : this.CRON_DAYS[dayOfWeek];
      this.cronFrequency = dayOfWeek === '*' ? CRON_OPTIONS.DAY : CRON_OPTIONS.WEEK;
    } else if (this.trigger.type === AUTOMATION_TRIGGER_TYPES.MANUAL) {
      this.inputFields = (automation.trigger as ManualAutomationTrigger).data.inputFields ?? [];
    } else if (this.trigger.type === AUTOMATION_TRIGGER_TYPES.EVENT && 'config' in automation.trigger.data) {
      this.eventTriggerConfig = automation.trigger.data.config ?? {};
    }

    const lookupIds: Record<string, string[]> = {};

    // Populate lookupIds from main conditions
    this.conditionsToFilterMap(lookupIds, automation.conditions as SimpleConditionsWrapper);

    // Collect lookup IDs from steps
    this.collectLookupIdsFromSteps(this.steps, lookupIds);

    // Collect lookup IDs from branch conditions
    for (const step of this.steps) {
      if (step.type === AUTOMATION_STEP_TYPES.BRANCHES) {
        for (const branch of step.data.branches) {
          // Populate lookupIds from branch conditions
          this.conditionsToFilterMap(lookupIds, branch.conditions as SimpleConditionsWrapper);
        }
      }
    }

    // Get lookup values from API
    this.lookups = await this.apiService.postPromise('/saved-filters/get-lookup-values', lookupIds);

    // Add static lookups
    this.lookups = {
      ...this.lookups,
      isNew: { true: 'Yes', false: 'No' },
      isWorkingTime: { true: 'Yes', false: 'No' },
      isAutoresponse: { true: 'Yes', false: 'No' },
    };

    // Hydrate main filters using the helper
    const flatMainFilters = this.transformConditionsToFlatRecord(
      automation.conditions?.type === CONDITIONS_TYPES.SIMPLE ? automation.conditions : undefined,
    );
    this.filters = await reconstructAndHydrateUiFilters(
      flatMainFilters,
      this.lookups,
      this.filterOptions,
      this.apiService,
      this.customFieldsService,
    );

    // Hydrate branch filters using the helper
    for (const step of this.steps) {
      if (step.type === AUTOMATION_STEP_TYPES.BRANCHES) {
        for (const branch of step.data.branches) {
          // 1. Transform branch conditions
          const flatBranchFilters = this.transformConditionsToFlatRecord(
            branch.conditions?.type === CONDITIONS_TYPES.SIMPLE ? branch.conditions : undefined,
          );
          // 2. Hydrate and assign hydrated filters back to the branch object
          branch.filters = await reconstructAndHydrateUiFilters(
            flatBranchFilters,
            this.lookups,
            this.filterOptions,
            this.apiService,
            this.customFieldsService,
          );
        }
      }
    }
  }

  private handleTicketTypeFieldFilter(clause: SimpleConditions, lookupIds: Record<string, string[]>) {
    const fieldIdMatch = (clause as SimpleConditionsLeaf).leftValueFrom.match(/ticketTypeFields\['(.+?)'\]/);
    const fieldId = fieldIdMatch?.[1];
    if (fieldId) {
      if (!lookupIds['customFieldId']) {
        lookupIds['customFieldId'] = [];
      }
      // Only collect the field ID itself for lookup
      lookupIds['customFieldId'].push(fieldId);
      // Note: If the custom field's *value* needs lookup (e.g., user select),
      // that lookup ID needs to be added separately based on field type.
      // This simplified version assumes we only need the custom field definition itself.
    } else {
      console.error('Possible malformed automation - ticket type field not found', {
        clause,
        automationId: this.id,
      });
    }
  }

  /**
   * Processes standard filters (non-custom fields)
   */
  private handleStandardFilter(filterTypeId: string, clause: SimpleConditions, lookupIds: Record<string, string[]>) {
    const option = this.filterOptions.find((obj) => obj.id === filterTypeId);

    if (!option) {
      console.error('Filter option not found', {
        filterTypeId,
        automationId: this.id,
      });

      return;
    }
    const values = (clause as SimpleConditionsLeaf).rightValue;

    if (values && Array.isArray(values) && values.length > 0) {
      if (!lookupIds[filterTypeId]) {
        lookupIds[filterTypeId] = [];
      }

      lookupIds[filterTypeId].push(...values);
    }
  }

  /**
   * UPDATE: Converts database conditions to UI filter format *only for collecting lookup IDs*.
   */
  conditionsToFilterMap(lookupIds: Record<string, string[]>, conditions?: SimpleConditionsWrapper) {
    const filter: any = conditions?.simple;

    const SUPPORTED_OPERATORS = [
      SIMPLE_CONDITIONS_OPERATORS.IN,
      SIMPLE_CONDITIONS_OPERATORS.CONTAINS,
      SIMPLE_CONDITIONS_OPERATORS.EQUALS,
      SIMPLE_CONDITIONS_OPERATORS.NOT_CONTAINS,
      SIMPLE_CONDITIONS_OPERATORS.NOT_IN,
      SIMPLE_CONDITIONS_OPERATORS.NOT_EQUALS,
      SIMPLE_CONDITIONS_OPERATORS.IS_NULL,
      SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL,
    ];

    if (filter?.and?.length) {
      filter.and.forEach((clause) => {
        if (!SUPPORTED_OPERATORS.includes(clause.operator)) {
          return;
        }

        const [valuePartsPrefix, ...valuePartsSuffix] = clause.leftValueFrom.split('.');
        const valueParts = [valuePartsPrefix, valuePartsSuffix.join('.')];

        if (valueParts[1]?.startsWith('ticketTypeFields')) {
          // Call restored helper
          this.handleTicketTypeFieldFilter(clause, lookupIds);
        } else {
          const filterTypeId = valueParts[1] ?? valueParts[0];
          // Call restored helper
          this.handleStandardFilter(filterTypeId, clause, lookupIds);
        }
      });
    }
  }

  /**
   * Recursively collects all lookup IDs from automation steps and their branches.
   * Used to fetch necessary data for displaying user-friendly labels.
   */
  collectLookupIdsFromSteps(steps: AutomationStep[], lookupIds: Record<string, string[]>) {
    for (const step of steps) {
      if (step.type === AUTOMATION_STEP_TYPES.BRANCHES) {
        // Only collect from steps within branch, skip conditions as they're handled separately
        for (const branch of step.data.branches) {
          if (branch.steps?.length) {
            this.collectLookupIdsFromSteps(branch.steps, lookupIds);
          }
        }
      } else if (step.type === AUTOMATION_STEP_TYPES.ACTION) {
        // Handle action steps
        const action = step.data.action;
        if (!action) continue;

        // Collect IDs based on action type
        switch (action.type) {
          case AUTOMATION_ACTION_TYPES.SET_SLACK_USER_GROUP_MEMBERSHIP:
            if (action.data?.slackUserGroupId) {
              if (!lookupIds['slackGroupId']) {
                lookupIds['slackGroupId'] = [];
              }
              lookupIds['slackGroupId'].push(action.data.slackUserGroupId);
            }

            if (action.data?.userIds?.length) {
              if (!lookupIds['assigneeId']) {
                lookupIds['assigneeId'] = [];
              }
              lookupIds['assigneeId'].push(...action.data.userIds);
            }

            break;
          case AUTOMATION_ACTION_TYPES.ASSIGN_CONVERSATION:
            if (action.data?.entityId) {
              if (!lookupIds['assigneeId']) {
                lookupIds['assigneeId'] = [];
              }
              if (!lookupIds['groupId']) {
                lookupIds['groupId'] = [];
              }
              if (!lookupIds['userGroupId']) {
                lookupIds['userGroupId'] = [];
              }

              if (
                typeof action.data.entityId === 'object' &&
                action.data.entityId.valueFrom &&
                action.data.entityId.valueFrom.startsWith('conversation.ticketTypeFields[')
              ) {
                const ticketTypeFieldId = extractTicketId(action.data.entityId.valueFrom);

                if (!lookupIds['customFieldId']) {
                  lookupIds['customFieldId'] = [];
                }

                lookupIds['customFieldId'].push(ticketTypeFieldId);
              } else if (typeof action.data.entityId === 'object' && action.data.entityId.value) {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }

                if (!lookupIds['userGroupId']) {
                  lookupIds['userGroupId'] = [];
                }

                lookupIds['assigneeId'].push(action.data.entityId.value);
                lookupIds['userGroupId'].push(action.data.entityId.value);
              } else if (typeof action.data.entityId === 'string') {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }

                if (!lookupIds['userGroupId']) {
                  lookupIds['userGroupId'] = [];
                }

                lookupIds['assigneeId'].push(action.data.entityId);
                lookupIds['userGroupId'].push(action.data.entityId);
              }
            }

            break;
          case AUTOMATION_ACTION_TYPES.SET_CONVERSATION_TICKET_TYPE:
            if (action.data?.ticketTypeId) {
              if (!lookupIds['ticketTypeId']) {
                lookupIds['ticketTypeId'] = [];
              }
              lookupIds['ticketTypeId'].push(action.data.ticketTypeId);
            }
            break;
          case AUTOMATION_ACTION_TYPES.ADD_CONVERSATION_TAGS:
            if (action.data?.tagIds?.length) {
              if (!lookupIds['tagId']) {
                lookupIds['tagId'] = [];
              }
              lookupIds['tagId'].push(...action.data.tagIds);
            }
            break;
          case AUTOMATION_ACTION_TYPES.SEND_SLACK_TEMPLATE:
            if (action.data?.templateId) {
              if (!lookupIds['templateId']) {
                lookupIds['templateId'] = [];
              }
              lookupIds['templateId'].push(action.data.templateId);
            }

            if (action.data?.entityId) {
              if (
                typeof action.data.entityId === 'object' &&
                action.data.entityId.valueFrom &&
                action.data.entityId.valueFrom.startsWith('conversation.ticketTypeFields[')
              ) {
                const ticketTypeFieldId = extractTicketId(action.data.entityId.valueFrom);

                if (!lookupIds['customFieldId']) {
                  lookupIds['customFieldId'] = [];
                }

                lookupIds['customFieldId'].push(ticketTypeFieldId);
              } else if (typeof action.data.entityId === 'object' && action.data.entityId.value) {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }

                if (!lookupIds['userGroupId']) {
                  lookupIds['userGroupId'] = [];
                }

                lookupIds['assigneeId'].push(action.data.entityId.value);
                lookupIds['userGroupId'].push(action.data.entityId.value);
              } else if (typeof action.data.entityId === 'string') {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }

                if (!lookupIds['userGroupId']) {
                  lookupIds['userGroupId'] = [];
                }

                lookupIds['assigneeId'].push(action.data.entityId);
                lookupIds['userGroupId'].push(action.data.entityId);
              }
            }

            break;
          case AUTOMATION_ACTION_TYPES.CHANGE_CONVERSATION_SUB_STATUS:
            if (action.data?.subStatusKey) {
              if (!lookupIds['subStatusKey']) {
                lookupIds['subStatusKey'] = [];
              }
              lookupIds['subStatusKey'].push(action.data.subStatusKey);
            }
            break;
          case AUTOMATION_ACTION_TYPES.ADD_USER_TO_GOOGLE_GROUP:
            if (action.data?.userId) {
              if (
                typeof action.data.userId === 'object' &&
                action.data.userId.valueFrom &&
                action.data.userId.valueFrom.startsWith('conversation.ticketTypeFields[')
              ) {
                const ticketTypeFieldId = extractTicketId(action.data.userId.valueFrom);

                if (!lookupIds['customFieldId']) {
                  lookupIds['customFieldId'] = [];
                }

                lookupIds['customFieldId'].push(ticketTypeFieldId);
              } else if (typeof action.data.userId === 'object' && action.data.userId.value) {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }

                lookupIds['assigneeId'].push(action.data.userId.value);
              }
            } else if (typeof action.data.userId === 'string') {
              if (!lookupIds['assigneeId']) {
                lookupIds['assigneeId'] = [];
              }

              lookupIds['assigneeId'].push(action.data.userId);
            }
            break;
          case AUTOMATION_ACTION_TYPES.GRANT_OKTA_GROUP_ACCESS:
          case AUTOMATION_ACTION_TYPES.REVOKE_OKTA_GROUP_ACCESS:
          case AUTOMATION_ACTION_TYPES.ASSIGN_OKTA_APPLICATION_ACCESS:
          case AUTOMATION_ACTION_TYPES.REVOKE_OKTA_APPLICATION_ACCESS:
            if (action.data?.userId) {
              if (
                typeof action.data.userId === 'object' &&
                action.data.userId.valueFrom &&
                action.data.userId.valueFrom.startsWith('conversation.ticketTypeFields[')
              ) {
                const ticketTypeFieldId = extractTicketId(action.data.userId.valueFrom);

                if (!lookupIds['customFieldId']) {
                  lookupIds['customFieldId'] = [];
                }

                lookupIds['customFieldId'].push(ticketTypeFieldId);
              } else if (typeof action.data.userId === 'object' && action.data.userId.value) {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }

                lookupIds['assigneeId'].push(action.data.userId.value);
              } else if (typeof action.data.userId === 'string') {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }

                lookupIds['assigneeId'].push(action.data.userId);
              }
            }
            break;
          case AUTOMATION_ACTION_TYPES.POST_TO_TRIAGE:
            if (action.data?.channelId) {
              if (!lookupIds['slackChannelId']) {
                lookupIds['slackChannelId'] = [];
              }
              lookupIds['slackChannelId'].push(action.data.channelId);
            }

            if (action.data?.tag?.entityId) {
              if (!lookupIds['assigneeId']) {
                lookupIds['assigneeId'] = [];
              }
              if (!lookupIds['userGroupId']) {
                lookupIds['userGroupId'] = [];
              }
              lookupIds['assigneeId'].push(action.data.tag.entityId);
              lookupIds['userGroupId'].push(action.data.tag.entityId);
            }
            break;
          case AUTOMATION_ACTION_TYPES.CREATE_APPROVAL_REQUEST:
            if (action.data?.entityId) {
              if (!lookupIds['assigneeId']) {
                lookupIds['assigneeId'] = [];
              }
              if (!lookupIds['userGroupId']) {
                lookupIds['userGroupId'] = [];
              }
              lookupIds['assigneeId'].push(action.data.entityId);
              lookupIds['userGroupId'].push(action.data.entityId);
            }
            break;
          case AUTOMATION_ACTION_TYPES.ADD_CONVERSATION_FOLLOWER:
            if (action.data?.entityId) {
              if (!lookupIds['assigneeId']) {
                lookupIds['assigneeId'] = [];
              }
              if (!lookupIds['userGroupId']) {
                lookupIds['userGroupId'] = [];
              }
              lookupIds['assigneeId'].push(action.data.entityId);
              lookupIds['userGroupId'].push(action.data.entityId);
            }
            break;
          case AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD:
            const fieldId = action.data?.customFieldId;

            // Collect the field ID itself
            if (fieldId) {
              if (!lookupIds['customFieldId']) {
                lookupIds['customFieldId'] = [];
              }

              lookupIds['customFieldId'].push(fieldId);

              if (
                action.data.value &&
                typeof action.data.value === 'object' &&
                !Array.isArray(action.data.value) &&
                action.data.value.valueFrom &&
                action.data.value.valueFrom.startsWith('conversation.ticketTypeFields[')
              ) {
                const ticketTypeFieldId = extractTicketId(action.data.value.valueFrom);

                if (!lookupIds['customFieldId']) {
                  lookupIds['customFieldId'] = [];
                }

                lookupIds['customFieldId'].push(ticketTypeFieldId);
              } else {
                const fieldValue = action.data?.value?.value ?? action.data?.value;

                // Now, check the actual field type to determine where to put the value ID(s)
                const customField = this.allCustomFieldsMap.get(fieldId);

                if (customField && fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
                  const valuesToLookup = (Array.isArray(fieldValue) ? fieldValue : [fieldValue])
                    .filter((v) => v !== null && v !== undefined && v !== '')
                    .map(String);

                  if (valuesToLookup.length > 0) {
                    switch (customField.type) {
                      case CUSTOM_FIELD_TYPES.USER_SELECT:
                      case CUSTOM_FIELD_TYPES.MULTI_USER_SELECT:
                        if (!lookupIds['assigneeId']) {
                          lookupIds['assigneeId'] = [];
                        }

                        lookupIds['assigneeId'].push(...valuesToLookup);

                        break;
                      case CUSTOM_FIELD_TYPES.USER_GROUP_SELECT:
                      case CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT:
                        // Add to multiple potential lookup keys for groups
                        const groupKeys = ['groupId', 'userGroupId', 'slackGroupId'];

                        groupKeys.forEach((key) => {
                          if (!lookupIds[key]) {
                            lookupIds[key] = [];
                          }

                          lookupIds[key].push(...valuesToLookup);
                        });

                        break;
                      // Add case for external selects if they need specific lookup key
                      case CUSTOM_FIELD_TYPES.SINGLE_SELECT:
                      case CUSTOM_FIELD_TYPES.MULTI_SELECT:
                        if (customField.externalSelect) {
                          if (!lookupIds['externalSelect']) {
                            lookupIds['externalSelect'] = [];
                          }

                          lookupIds['externalSelect'].push(...valuesToLookup);
                        }

                        // Internal selects with primitive values don't need value lookup
                        break;
                      default:
                        // No value lookup needed for text, date, checkbox etc.
                        break;
                    }
                  }
                }
              }
            }
            break;
          case AUTOMATION_ACTION_TYPES.SET_CONVERSATION_COLLABORATOR:
            if (action.data?.collaboratorTypeId) {
              if (!lookupIds['collaboratorTypeId']) {
                lookupIds['collaboratorTypeId'] = [];
              }
              lookupIds['collaboratorTypeId'].push(action.data.collaboratorTypeId);
            }

            if (action.data?.entityId) {
              if (
                typeof action.data.entityId === 'object' &&
                action.data.entityId.valueFrom &&
                action.data.entityId.valueFrom.startsWith('conversation.ticketTypeFields[')
              ) {
                const ticketTypeFieldId = extractTicketId(action.data.entityId.valueFrom);

                if (!lookupIds['customFieldId']) {
                  lookupIds['customFieldId'] = [];
                }

                lookupIds['customFieldId'].push(ticketTypeFieldId);
              } else if (typeof action.data.entityId === 'object' && action.data.entityId.value) {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }

                if (!lookupIds['userGroupId']) {
                  lookupIds['userGroupId'] = [];
                }

                lookupIds['assigneeId'].push(action.data.entityId.value);
                lookupIds['userGroupId'].push(action.data.entityId.value);
              } else if (typeof action.data.entityId === 'string') {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }

                if (!lookupIds['userGroupId']) {
                  lookupIds['userGroupId'] = [];
                }

                lookupIds['assigneeId'].push(action.data.entityId);
                lookupIds['userGroupId'].push(action.data.entityId);
              }
            }

            break;
          case AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION:
            // Collect ticket type ID for CREATE_CONVERSATION actions
            if (action.data?.ticketTypeId) {
              let ticketTypeIdToLookup: string | null = null;

              // Handle different ticket type ID formats
              if (typeof action.data.ticketTypeId === 'string') {
                ticketTypeIdToLookup = action.data.ticketTypeId;
              } else if (typeof action.data.ticketTypeId === 'object' && 'value' in action.data.ticketTypeId) {
                ticketTypeIdToLookup = action.data.ticketTypeId.value;
              }
              // Skip merge fields (valueFrom) as they don't need lookups

              if (ticketTypeIdToLookup) {
                if (!lookupIds['ticketTypeId']) {
                  lookupIds['ticketTypeId'] = [];
                }
                lookupIds['ticketTypeId'].push(ticketTypeIdToLookup);
              }
            }

            // Collect project ID for CREATE_CONVERSATION actions
            if (action.data?.projectId) {
              let projectIdToLookup: string | null = null;

              if (typeof action.data.projectId === 'string') {
                projectIdToLookup = action.data.projectId;
              } else if (typeof action.data.projectId === 'object' && 'value' in action.data.projectId) {
                projectIdToLookup = action.data.projectId.value;
              }

              if (projectIdToLookup) {
                if (!lookupIds['projectId']) {
                  lookupIds['projectId'] = [];
                }
                lookupIds['projectId'].push(projectIdToLookup);
              }
            }

            // Collect submitter ID for CREATE_CONVERSATION actions
            if (action.data?.submitterId) {
              let submitterIdToLookup: string | null = null;

              if (typeof action.data.submitterId === 'string') {
                submitterIdToLookup = action.data.submitterId;
              } else if (typeof action.data.submitterId === 'object' && 'value' in action.data.submitterId) {
                submitterIdToLookup = action.data.submitterId.value;
              }

              if (submitterIdToLookup) {
                if (!lookupIds['userId']) {
                  lookupIds['userId'] = [];
                }
                lookupIds['userId'].push(submitterIdToLookup);
              }
            }

            // Collect customer ID for CREATE_CONVERSATION actions
            if (action.data?.customerId) {
              let customerIdToLookup: string | null = null;

              if (typeof action.data.customerId === 'string') {
                customerIdToLookup = action.data.customerId;
              } else if (typeof action.data.customerId === 'object' && 'value' in action.data.customerId) {
                customerIdToLookup = action.data.customerId.value;
              }

              if (customerIdToLookup) {
                if (!lookupIds['customerId']) {
                  lookupIds['customerId'] = [];
                }
                lookupIds['customerId'].push(customerIdToLookup);
              }
            }

            // Collect conversation link IDs for CREATE_CONVERSATION actions
            if (action.data?.conversationLinks && Array.isArray(action.data.conversationLinks)) {
              action.data.conversationLinks.forEach((link: any) => {
                if (link?.linkedConversationId) {
                  let conversationIdToLookup: string | null = null;

                  if (typeof link.linkedConversationId === 'string') {
                    conversationIdToLookup = link.linkedConversationId;
                  } else if (typeof link.linkedConversationId === 'object' && 'value' in link.linkedConversationId) {
                    conversationIdToLookup = link.linkedConversationId.value;
                  }
                  // Skip merge fields (valueFrom) as they don't need lookups and cause 500 errors

                  if (conversationIdToLookup) {
                    if (!lookupIds['conversationId']) {
                      lookupIds['conversationId'] = [];
                    }
                    lookupIds['conversationId'].push(conversationIdToLookup);
                  }
                }
              });
            }
            break;
          case AUTOMATION_ACTION_TYPES.CREATE_ESCALATION:
            // Collect escalation type ID for CREATE_ESCALATION actions
            if (action.data?.escalationTypeId) {
              if (!lookupIds['escalationTypeId']) {
                lookupIds['escalationTypeId'] = [];
              }
              lookupIds['escalationTypeId'].push(action.data.escalationTypeId);
            }

            // Collect assignee IDs for CREATE_ESCALATION actions
            if (action.data?.assignedToUserId) {
              let assigneeIdToLookup: string | null = null;

              if (typeof action.data.assignedToUserId === 'string') {
                assigneeIdToLookup = action.data.assignedToUserId;
              } else if (typeof action.data.assignedToUserId === 'object' && 'value' in action.data.assignedToUserId) {
                assigneeIdToLookup = action.data.assignedToUserId.value;
              }

              if (assigneeIdToLookup) {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }
                lookupIds['assigneeId'].push(assigneeIdToLookup);
              }
            }

            if ((action.data as any)?.assignedToGroupId) {
              let groupIdToLookup: string | null = null;

              if (typeof (action.data as any).assignedToGroupId === 'string') {
                groupIdToLookup = (action.data as any).assignedToGroupId;
              } else if (
                typeof (action.data as any).assignedToGroupId === 'object' &&
                'value' in (action.data as any).assignedToGroupId
              ) {
                groupIdToLookup = (action.data as any).assignedToGroupId.value;
              }

              if (groupIdToLookup) {
                if (!lookupIds['userGroupId']) {
                  lookupIds['userGroupId'] = [];
                }
                lookupIds['userGroupId'].push(groupIdToLookup);
              }
            }

            // Collect createdBy user ID for CREATE_ESCALATION actions
            if ((action.data as any)?.createdBy) {
              let createdByIdToLookup: string | null = null;

              if (typeof (action.data as any).createdBy === 'string') {
                createdByIdToLookup = (action.data as any).createdBy;
              } else if (
                typeof (action.data as any).createdBy === 'object' &&
                'value' in (action.data as any).createdBy
              ) {
                createdByIdToLookup = (action.data as any).createdBy.value;
              }

              if (createdByIdToLookup) {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }
                lookupIds['assigneeId'].push(createdByIdToLookup);
              }
            }
            break;
          case AUTOMATION_ACTION_TYPES.CREATE_PAGERDUTY_PAGE:
            if (action.data?.serviceId) {
              if (!lookupIds['serviceId']) {
                lookupIds['serviceId'] = [];
              }
              lookupIds['serviceId'].push(action.data.serviceId);
            }
            if ((action.data as any)?.requesterUserId) {
              let requesterUserIdToLookup: string | null = null;

              if (typeof (action.data as any).requesterUserId === 'string') {
                requesterUserIdToLookup = (action.data as any).requesterUserId;
              } else if (
                typeof (action.data as any).requesterUserId === 'object' &&
                'value' in (action.data as any).requesterUserId
              ) {
                requesterUserIdToLookup = (action.data as any).requesterUserId.value;
              }

              if (requesterUserIdToLookup) {
                if (!lookupIds['assigneeId']) {
                  lookupIds['assigneeId'] = [];
                }
                lookupIds['assigneeId'].push(requesterUserIdToLookup);
              }
            }
            break;
          // TODO: Add other action types as needed
        }
      }
    }
  }

  /**
   * Converts a value to the appropriate type for database storage
   */
  private convertValueForStorage(value: any, optionId: string): string | number | boolean {
    return optionId === 'priority' ? Number(value) : value;
  }

  private getCustomFieldOperator(
    option: FilterOption,
    fieldType: any,
  ): (typeof SIMPLE_CONDITIONS_OPERATORS)[keyof typeof SIMPLE_CONDITIONS_OPERATORS] {
    if (
      [
        CUSTOM_FIELD_TYPES.SINGLE_SELECT,
        CUSTOM_FIELD_TYPES.USER_SELECT,
        CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
        CUSTOM_FIELD_TYPES.DATE,
        CUSTOM_FIELD_TYPES.CHECKBOX,
      ].includes(fieldType)
    ) {
      return option.operator === FILTER_OPERATORS.NOT_EQUALS
        ? SIMPLE_CONDITIONS_OPERATORS.NOT_EQUALS
        : SIMPLE_CONDITIONS_OPERATORS.EQUALS;
    }

    return option.operator === FILTER_OPERATORS.NOT_EQUALS
      ? SIMPLE_CONDITIONS_OPERATORS.NOT_CONTAINS
      : SIMPLE_CONDITIONS_OPERATORS.CONTAINS;
  }

  private processCustomFieldValue(value: any, fieldType: string, isExternalSelect: boolean): any {
    // Handle null values
    if (value.values === null) {
      return null;
    }

    if (isExternalSelect) {
      if (fieldType === CUSTOM_FIELD_TYPES.SINGLE_SELECT) {
        return value.values?.id ?? null;
      }
      if (fieldType === CUSTOM_FIELD_TYPES.MULTI_SELECT) {
        return Array.isArray(value.values) ? value.values.map((v) => (v === null ? null : v.id)) : [];
      }
    }

    if (fieldType === CUSTOM_FIELD_TYPES.USER_SELECT || fieldType === CUSTOM_FIELD_TYPES.USER_GROUP_SELECT) {
      return value.values?.id ?? null;
    }
    if (
      fieldType === CUSTOM_FIELD_TYPES.MULTI_USER_SELECT ||
      fieldType === CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT
    ) {
      return Array.isArray(value.values) ? value.values.map((v) => (v === null ? null : v.id)) : [];
    }
    return value.values;
  }

  /**
   * Creates a condition for a custom field
   */
  private createCustomFieldCondition(option: FilterOption, value: any): SimpleConditions {
    const fieldType = value.additionalFields?.type;
    const isExternalSelect = value.additionalFields?.externalSelect;
    const processedValue = this.processCustomFieldValue(value, fieldType, isExternalSelect);

    // Use explicit isNull/isNotNull operators for null values
    if (processedValue === null) {
      return {
        leftValueFrom: `conversation.ticketTypeFields['${value.id}']`,
        operator:
          option.operator === FILTER_OPERATORS.NOT_EQUALS
            ? SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL
            : SIMPLE_CONDITIONS_OPERATORS.IS_NULL,
      };
    }

    // For arrays containing null values, we need to handle mixed scenarios
    if (Array.isArray(processedValue) && processedValue.includes(null)) {
      // If the array contains both null and non-null values, we need compound conditions
      const nonNullValues = processedValue.filter((v) => v !== null);
      const hasNull = processedValue.includes(null);

      if (nonNullValues.length === 0) {
        // Array contains only null values
        return {
          leftValueFrom: `conversation.ticketTypeFields['${value.id}']`,
          operator:
            option.operator === FILTER_OPERATORS.NOT_EQUALS
              ? SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL
              : SIMPLE_CONDITIONS_OPERATORS.IS_NULL,
        };
      }

      // Array contains both null and non-null values - use the original logic
      // This will be handled by the backend condition evaluation
    }

    return {
      leftValueFrom: `conversation.ticketTypeFields['${value.id}']`,
      operator: this.getCustomFieldOperator(option, fieldType),
      rightValue: processedValue,
    };
  }

  /**
   * Creates a condition for a standard field
   */
  private createStandardFieldCondition(
    option: FilterOption,
    valueIds: (string | number | boolean)[],
  ): SimpleConditions {
    return {
      leftValueFrom:
        option.id === 'isWorkingTime'
          ? 'isWorkingTime'
          : option.id === 'isAutoresponse'
            ? 'message.isAutoresponse'
            : `conversation.${option.id}`,
      operator: ['tagId', 'customerTagId', 'accountTagId'].includes(option.id)
        ? option.operator === FILTER_OPERATORS.NOT_EQUALS
          ? SIMPLE_CONDITIONS_OPERATORS.NOT_CONTAINS
          : SIMPLE_CONDITIONS_OPERATORS.CONTAINS
        : option.operator === FILTER_OPERATORS.NOT_EQUALS
          ? SIMPLE_CONDITIONS_OPERATORS.NOT_IN
          : SIMPLE_CONDITIONS_OPERATORS.IN,
      rightValue: valueIds,
    };
  }

  /**
   * Converts UI filter map to database conditions format
   */
  filterMapToConditions(
    filterMap: Map<FilterOption, { id: string | boolean; label: string; values?: any; additionalFields?: any }[]>,
  ): SimpleConditionsWrapper {
    const andedFilters: SimpleConditions[] = [];

    for (const [option, values] of filterMap.entries()) {
      // Convert values to appropriate types
      const valueIds = values.map((v) => this.convertValueForStorage(v.id, option.id));

      if (!valueIds.length) continue;

      if (option.id === 'customFieldId' || option.id.startsWith('ticketTypeFields')) {
        // Handle custom fields
        const customFieldConditions = values.map((value) => this.createCustomFieldCondition(option, value));
        andedFilters.push(...customFieldConditions);
      } else {
        // Handle standard fields
        andedFilters.push(this.createStandardFieldCondition(option, valueIds));
      }
    }

    return {
      type: CONDITIONS_TYPES.SIMPLE,
      simple: andedFilters.length ? { and: andedFilters } : {},
    };
  }

  async getFunctionsVersions() {
    try {
      const functionsVersionsResponse: any = await this.automationsService.getAllFunctionsVersions(this.projectId);

      this.functionsVersions = functionsVersionsResponse;

      this.functionNames = this.getFunctionsVersion('active').exportedFunctionsByType.automationAction;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  getFunctionsVersion(status: string) {
    return (this.functionsVersions ?? []).find((version) => version.status === status);
  }

  /**
   * Validates and saves the current automation configuration.
   * Performs necessary transformations and validations before saving.
   */
  async save(dismiss?: boolean) {
    const hasPermission =
      this.authService.isTenantAdmin ||
      this.authService.projects.some(
        (p) => p.role === PROJECT_MEMBER_ROLES.OWNER || p.role === PROJECT_MEMBER_ROLES.AGENT,
      );

    if (!hasPermission) {
      await this.msgSrvc.show('You do not have permission to edit this automation');
      return;
    }

    if (!this.name) {
      await this.msgSrvc.show('Please enter a name.');
      return;
    }

    if (this.trigger.type === AUTOMATION_TRIGGER_TYPES.EVENT && !this.trigger.data?.event) {
      await this.msgSrvc.show('Please select a type of event in the Trigger section');
      return;
    }

    if (!this.steps?.length) {
      await this.msgSrvc.show('Please add at least one step to the automation');
      return;
    }

    this.isLoading = true;

    try {
      // Use the centralized serialization method
      const automationState = this.serializeAutomationState();

      // Extract updates (exclude id and projectId for updates)
      const { id, projectId, ...updates } = automationState;

      if (this.id) {
        await this.automationsService.updatePartial(this.id, {
          ...updates,
        });

        if (dismiss) {
          await this.msgSrvc.show('Updated successfully');
        }
      } else {
        const newAutomation = await this.automationsService.create({
          ...updates,
          projectId: this.projectId,
        });

        this.id = newAutomation.id;

        await this.loadAutomation();

        this.navCtrl.navigateRoot(`/dashboard/automations/${this.id}`);
        return;
      }

      this.updatedAt = new Date();
      this.shouldShowDismissConfirmation = false;
      this.msgSrvc.show('Updated!', { position: 'bottom' });

      if (dismiss) {
        this.dismiss();
      }
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader: `This can't be undone`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.automationsService.delete(this.id);
              this.isLoading = false;
              this.shouldShowDismissConfirmation = false;
              this.dismiss();
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.show(`Unable to delete automation: ${e}`);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async addFilter() {
    this.filterButton.addFilter();
  }

  async dismiss() {
    if (this.shouldShowDismissConfirmation) {
      const alert = await this.alertCtrl.create({
        header: 'Go back?',
        subHeader: `Your changes will be lost`,
        buttons: [
          {
            text: 'Stay',
          },
          {
            text: 'Leave',
            handler: async () => {
              this.navCtrl.navigateRoot('/dashboard/automations');
            },
          },
        ],
      });
      alert.present();
    } else {
      this.navCtrl.navigateRoot('/dashboard/automations');
    }
  }

  async addStep(index: number, branchStep?: any, event?: any) {
    this.searchablePopoverService.automationActions({
      event, // Pass the click event that triggered this method
      actionOptions: this.actionOptions,
      callback: (_selectedId: string, _title: string, metadata: any) => {
        if (!metadata) {
          return;
        }

        this.insertStepAtIndex(index, metadata, branchStep, !branchStep && index === this.steps.length);
      },
    });
  }

  addBranch(stepIndex: number) {
    const step = this.steps[stepIndex];

    if (step.type === AUTOMATION_STEP_TYPES.BRANCHES) {
      step.data.branches.push({
        conditions: {
          type: CONDITIONS_TYPES.SIMPLE,
          simple: {},
        },
        steps: [],
        filters: new Map(),
      });
    }
  }

  removeBranch(stepIndex: number, branchIndex: number) {
    const step = this.steps[stepIndex];
    if (step.type === AUTOMATION_STEP_TYPES.BRANCHES) {
      step.data.branches.splice(branchIndex, 1);
    }
  }

  /**
   * Handles the insertion of new steps into the automation workflow.
   * Supports adding steps both at the root level and within branches.
   */
  setCreateConversationDefaults(actionData: any) {
    // Set default merge field values for EVENT triggers
    if (
      !actionData.title ||
      actionData.title === '' ||
      (typeof actionData.title === 'object' && !actionData.title.value && !actionData.title.valueFrom)
    ) {
      actionData.title = {
        value:
          '<span data-label="Conversation Title" data-from="conversation.title" data-type="variable">Automation ticket</span> ',
      };
    }
    // No default description - let users add their own content
    if (!actionData.status) {
      actionData.status = { valueFrom: 'conversation.status' };
    }
    if (!actionData.priority) {
      actionData.priority = { valueFrom: 'conversation.priority' };
    }
    if (!actionData.projectId) {
      actionData.projectId = { valueFrom: 'conversation.projectId' };
    }
    if (!actionData.submitterId) {
      actionData.submitterId = { valueFrom: 'conversation.submitterUserId' };
    }
    if (!actionData.ticketTypeId) {
      actionData.ticketTypeId = { valueFrom: 'conversation.ticketTypeId' };
    }
    if (!actionData.postingType) {
      actionData.postingType = 'channel';
    }
  }

  setCreateEscalationDefaults(actionData: any) {
    // Set default merge field values for EVENT triggers
    if (
      !actionData.title ||
      actionData.title === '' ||
      (typeof actionData.title === 'object' && !actionData.title.value && !actionData.title.valueFrom)
    ) {
      actionData.title = {
        value:
          '<span data-label="Conversation Title" data-from="conversation.title" data-type="variable">Automation escalation</span> ',
      };
    }
    if (!actionData.priority) {
      actionData.priority = { valueFrom: 'conversation.priority' };
    }
    if (!actionData.createdBy) {
      actionData.createdBy = null;
    }
  }

  setCreatePagerDutyPageDefaults(actionData: any) {
    if (
      !actionData.title ||
      actionData.title === '' ||
      (typeof actionData.title === 'object' && !actionData.title.value && !actionData.title.valueFrom)
    ) {
      actionData.title = {
        value:
          '<span data-label="Conversation Title" data-from="conversation.title" data-type="variable">PagerDuty page</span> ',
      };
    }
    if (!actionData.requesterUserId) {
      // Use integration default email as requesterUserId (this is the default fallback behavior for this field when null)
      actionData.requesterUserId = null;
    }
  }

  insertStepAtIndex(index: number, metadata: any, branch?: any, scrollToBottom?: boolean) {
    if (metadata.stepType === AUTOMATION_STEP_TYPES.BRANCHES && branch) {
      this.msgSrvc.show('You cannot add branches within a branch');
      return;
    }

    // Deep clone
    const newData = structuredClone(metadata);

    // Set defaults for CREATE_CONVERSATION actions when trigger is EVENT
    if (newData.action?.type === AUTOMATION_ACTION_TYPES.CREATE_CONVERSATION && this.trigger.type === 'event') {
      this.setCreateConversationDefaults(newData.action.data);
    }

    if (newData.action?.type === AUTOMATION_ACTION_TYPES.CREATE_ESCALATION) {
      this.setCreateEscalationDefaults(newData.action.data);
    }

    if (newData.action?.type === AUTOMATION_ACTION_TYPES.CREATE_PAGERDUTY_PAGE) {
      this.setCreatePagerDutyPageDefaults(newData.action.data);
    }

    const object = {
      type: newData.stepType || AUTOMATION_STEP_TYPES.ACTION,
      data: newData,
    };

    this.animationStep = object;

    if (branch) {
      // When adding to a branch, insert at the specified index
      branch.steps.splice(index, 0, object);
    } else {
      this.steps.splice(index, 0, object);
    }

    this.shouldShowDismissConfirmation = true;

    if (scrollToBottom) {
      setTimeout(() => {
        this.content.scrollToBottom(300);
      }, 100);
    }
  }

  removeStep(i: number, branch?: any) {
    this.animationStep = this.steps[i];

    if (branch) {
      branch.steps.splice(i, 1);
    } else {
      this.steps.splice(i, 1);
    }
  }

  copyToClipboard(propertyName: string) {
    navigator.clipboard
      .writeText(propertyName)
      .then(() => {
        this.msgSrvc.show('Copied to clipboard');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  chooseConversation() {
    this.searchablePopoverService.conversations({
      event,
      projectId: this.projectId,
      callback: async (id: string) => {
        if (id) {
          this.testObjectId = id;
        }
      },
    });
  }

  /**
   * Executes a test run of the automation with the selected conversation.
   * Handles different trigger types and displays test results.
   */
  async runTest() {
    this.testIsRunning = true;
    this.testLogs = [];

    try {
      if (this.trigger.type === AUTOMATION_TRIGGER_TYPES.WEBHOOK) {
        // Make a POST request to the webhook URL
        await fetch(this.getWebhookUrl(this.id), {
          method: 'POST',
          body: JSON.stringify({ sampleKey: 'sampleValue' }),
        });
        this.testLogs = [{ message: 'Request sent! It may take a few seconds to show up in the history.' }];
        this.testIsRunning = false;
      } else {
        // First save latest code to server
        await this.save(false);

        // Create the run
        const result = await this.automationsService.runAutomationForConversation(this.id, this.testObjectId);
        this.testRunId = result.runs[0].id;

        await this.getTestLogs();

        this.testIsRunning = false;
      }
    } catch (e) {
      this.msgSrvc.showError(e);
      this.testIsRunning = false;
    }
  }

  async getTestLogs() {
    // Get the logs for that run
    const logs = await this.automationsService.listLogEntriesForRun(this.id, this.testRunId);

    // Show a placeholder if we don't have any (kinda jank solution)
    this.testLogs = logs?.length ? logs : [{ message: 'No logs yet...' }];
  }

  getWebhookUrl(id: string) {
    return `https://api.unthread.io/api/webhooks/automations/${this.authService.tenantId}/${id}`;
  }

  copyWebhookUrlToClipboard(id: string) {
    navigator.clipboard
      .writeText(this.getWebhookUrl(id))
      .then(() => {
        this.msgSrvc.show('Copied!');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  formatTriggerEvent(triggerEvent: string) {
    // this is a quick hack to avoid time consuming key replace in backend
    if (triggerEvent === 'internalUnresponsive') {
      triggerEvent = 'agentUnresponsive';
    } else if (triggerEvent === 'externalUnresponsive') {
      triggerEvent = 'customerUnresponsive';
    } else if (triggerEvent === 'internalMessageAdded') {
      triggerEvent = 'agentMessageAdded';
    } else if (triggerEvent === 'externalMessageAdded') {
      triggerEvent = 'customerMessageAdded';
    }
    return _.snakeCase(triggerEvent).replace(/_/g, ' ');
  }

  triggerFilterCompareFn(o1: any, o2: any) {
    return (o1 && o2 && _.isEqual(o1, o2)) || (o1 === '' && o2 == null);
  }

  getTriggerKey() {
    if (this.trigger.type === AUTOMATION_TRIGGER_TYPES.EVENT) {
      return `event:${this.trigger.data.objectType}:${this.trigger.data.event}`;
    }

    if (this.trigger.type === AUTOMATION_TRIGGER_TYPES.MANUAL) {
      return `manual:${this.trigger.data.objectType}`;
    }

    return this.trigger.type;
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
  }

  branchFiltersDidChange(selected: SelectedFilterMap, branch: any) {
    branch.filters = selected;
  }

  actionDidChange(action: any) {
    // Lazy way of setting initial values
    this.actionOptions.some((option) => {
      if (option.metadata.action.type === action.type) {
        action.data = option.metadata.action.data;
        return true;
      }
      return false;
    });
  }

  displayDateFrom(date: Date) {
    return moment(date).fromNow();
  }

  async getRunHistory() {
    if (!this.id) {
      return;
    }

    this.isLoadingRuns = true;
    const automationRunsResponse = await this.automationsService.listRunsForAutomation(this.id, {
      limit: 250,
      order: ['startedAt'],
      descending: true,
    });
    this.isLoadingRuns = false;
    this.automationRuns = automationRunsResponse.data;
  }

  async viewLogsForAutomationRun(run: Partial<AutomationRun>) {
    const modal = await this.modalCtrl.create({
      component: AutomationLogsComponent,
      cssClass: 'modal-large',
      componentProps: {
        run,
      },
    });

    modal.present();
  }

  getEventsForTrigger(objectType: string) {
    return Object.values(AUTOMATION_TRIGGER_EVENTS[objectType ?? AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION]);
  }

  async openEmojiPicker() {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: (emoji: any) => {
          popover.dismiss();

          // Handle native and custom emojis
          const value = emoji.native ?? `:${emoji.id}:`;

          const trigger = this.trigger as SlackReactionAutomationTrigger;

          if (!trigger.data.emojis) {
            trigger.data.emojis = [];
          }

          trigger.data.emojis.push(value);
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event,
    });

    popover.present();
  }

  removeEmoji(i: number) {
    (this.trigger as SlackReactionAutomationTrigger).data.emojis.splice(i, 1);
  }

  async editTime() {
    // TODO: not create a popover every time you click
    // These are not getting removed from memory properly
    const popover = await this.popoverCtrl.create({
      component: TimePickerComponent,
      componentProps: {
        callback: (time: string) => {
          this.cronTimeDisplay = time;
          this.cronTime = getMilitaryTime(time);
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  compareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  async onActiveToggleChanged() {
    if (this.isInitializing || !this.id) {
      return;
    }

    const loader = await this.loadingCtrl.create({
      message: this.isActive ? 'Activating...' : 'Deactivating...',
      spinner: 'crescent',
    });

    loader.present();

    await this.automationsService.updatePartial(this.id, {
      status: this.isActive ? 'active' : 'draft',
    });

    loader.dismiss();
  }

  /**
   * Manages drag-and-drop functionality for reordering steps and adding new steps.
   * Handles both existing steps and new steps from the action options panel.
   */
  onDragActionEnd(event: DragEvent, actionIndex: number, sourceBranch?: any, _branchActionIndex?: number) {
    if (this.draggingOverStepIndex !== null) {
      this.animationStep = undefined;

      let reorderedItem;

      if (sourceBranch) {
        // Moving a step within or from a branch
        reorderedItem = sourceBranch.steps.splice(actionIndex, 1)[0];
      } else {
        // Moving a step from the main steps array
        reorderedItem = this.steps.splice(actionIndex, 1)[0];
      }

      if (this.draggingOverBranch) {
        // Adding to a branch
        if (this.draggingOverBranch === sourceBranch) {
          // Moving within the same branch
          let insertIndex = Math.max(0, this.draggingOverStepIndex);
          if (insertIndex > actionIndex) {
            insertIndex--;
          }
          this.draggingOverBranch.steps.splice(insertIndex, 0, reorderedItem);
        } else {
          // Moving to a different branch
          let insertIndex = Math.max(0, this.draggingOverStepIndex);
          this.draggingOverBranch.steps.splice(insertIndex, 0, reorderedItem);
        }
      } else {
        // Adding to main steps array
        let insertIndex = Math.max(0, this.draggingOverStepIndex);
        if (insertIndex > actionIndex) {
          insertIndex--;
        }
        this.steps.splice(insertIndex, 0, reorderedItem);
      }

      this.animationStep = reorderedItem;
    }

    this.draggingOverStepIndex = null;
    this.draggingOverBranch = null;
  }

  onDragOptionEnd(_event: DragEvent, optionIndex: number) {
    if (this.draggingOverStepIndex !== null) {
      this.animationStep = undefined;
      const stepToAdd = this.filteredActionOptions[optionIndex].metadata;

      if (this.draggingOverBranch) {
        // Adding to a branch at a specific position
        const insertIndex = Math.max(0, this.draggingOverStepIndex);
        this.insertStepAtIndex(insertIndex, stepToAdd, this.draggingOverBranch, false);
      } else {
        // Adding to main steps array
        this.insertStepAtIndex(this.draggingOverStepIndex, stepToAdd, null, false);
      }
    }

    this.draggingOverStepIndex = null;
    this.draggingOverBranch = null;
  }

  onDragOver(event: DragEvent, stepIndex: number, branch?: any) {
    event.preventDefault();
    event.stopPropagation();
    this.draggingOverStepIndex = stepIndex;
    this.draggingOverBranch = branch;
  }

  onDragOut(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.draggingOverStepIndex = null;
    this.draggingOverBranch = null;
  }

  get filteredActionOptions() {
    let filtered = this.actionOptions;

    // Filter by category (filter tabs)
    if (this.actionsFilterType !== 'all') {
      filtered = filtered.filter((option) => option.metadata?.category === this.actionsFilterType);
    }

    // Filter by search term
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (option) =>
          option.label.toLowerCase().includes(search) || option.metadata?.description?.toLowerCase().includes(search),
      );
    }

    return filtered;
  }

  /**
   * Transforms the backend SimpleConditionsWrapper format into the flat
   * Record<string, string[]> format expected by reconstructAndHydrateUiFilters.
   */
  private transformConditionsToFlatRecord(
    conditions?: SimpleConditionsWrapper,
  ): { key: string; operator: SimpleConditionsOperator; values: string[] }[] {
    const flatFilters: { key: string; operator: SimpleConditionsOperator; values: string[] }[] = [];
    const filter: any = conditions?.simple;

    const SUPPORTED_OPERATORS = [
      SIMPLE_CONDITIONS_OPERATORS.IN,
      SIMPLE_CONDITIONS_OPERATORS.CONTAINS,
      SIMPLE_CONDITIONS_OPERATORS.EQUALS,
      SIMPLE_CONDITIONS_OPERATORS.NOT_EQUALS,
      SIMPLE_CONDITIONS_OPERATORS.NOT_CONTAINS,
      SIMPLE_CONDITIONS_OPERATORS.NOT_IN,
      SIMPLE_CONDITIONS_OPERATORS.IS_NULL,
      SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL,
    ];

    if (filter?.and?.length) {
      filter.and.forEach((clause) => {
        if (!SUPPORTED_OPERATORS.includes(clause.operator)) {
          return;
        }

        const [valuePartsPrefix, ...valuePartsSuffix] = clause.leftValueFrom.split('.');
        let filterKey: string;

        // Check if it's a custom field key
        const customFieldMatch = clause.leftValueFrom.match(/^conversation\.ticketTypeFields\['(.+?)'\]$/);
        if (customFieldMatch) {
          filterKey = `ticketTypeFields.${customFieldMatch[1]}`;
        } else {
          // Otherwise, treat as a standard field key
          filterKey = valuePartsSuffix.join('.') || valuePartsPrefix;
        }

        // Handle null operators that don't have rightValue
        if (
          clause.operator === SIMPLE_CONDITIONS_OPERATORS.IS_NULL ||
          clause.operator === SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL
        ) {
          flatFilters.push({
            key: filterKey,
            operator: clause.operator,
            values: ['null'], // Use 'null' as a placeholder value for UI hydration
          });
        } else {
          // Ensure rightValue is always an array for consistency
          const values = Array.isArray(clause.rightValue) ? clause.rightValue : [clause.rightValue];

          if (values.length > 0) {
            flatFilters.push({
              key: filterKey,
              operator: clause.operator,
              values,
            });
          }
        }
      });
    }

    return flatFilters;
  }

  // Assistant chat methods
  convertToMarkdown(text: string): string {
    return this.markdown.render(text);
  }

  /**
   * Serializes the current automation state into the format expected by the backend
   * This method encapsulates all the serialization logic used by save operations
   */
  private serializeAutomationState(): Partial<Automation> {
    // Process main conditions
    const conditions = this.filterMapToConditions(this.filters);

    // Create a deep copy of steps to avoid mutating original state
    const serializedSteps = structuredClone(this.steps);

    // Process branch conditions for any branching steps
    for (const step of serializedSteps) {
      if (step.type === AUTOMATION_STEP_TYPES.BRANCHES) {
        for (const branch of step.data.branches) {
          // Handle null filters from backend by converting to empty Map
          const filters = branch.filters || new Map();
          branch.conditions = this.filterMapToConditions(filters);
        }
      }
    }

    // Create a copy of trigger to avoid mutating original state
    const serializedTrigger = structuredClone(this.trigger);

    // Process trigger-specific data
    if (serializedTrigger.type === AUTOMATION_TRIGGER_TYPES.SCHEDULED) {
      const [hour, minute] = this.cronTime.split(':').map(Number);
      const dayOfWeek = this.cronFrequency === CRON_OPTIONS.WEEK ? CRON_DAYS_OPTIONS[this.cronDay] : '*';

      serializedTrigger.data = {
        cron: `${minute} ${hour} * * ${dayOfWeek}`,
        timezone: this.timezone,
        workingTimeOnly: false,
      };
    }

    if (serializedTrigger.type === AUTOMATION_TRIGGER_TYPES.MANUAL) {
      const manualTrigger = serializedTrigger as ManualAutomationTrigger;
      manualTrigger.data.inputFields = this.inputFields.map((inputField) => ({
        ...inputField,
        options: inputField.externalSelect != null ? null : inputField.options,
      }));
    }

    if (serializedTrigger.type === AUTOMATION_TRIGGER_TYPES.EVENT) {
      serializedTrigger.data.config = this.eventTriggerConfig;
    }

    // Return the serialized automation state
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      status: this.isActive ? 'active' : 'draft',
      steps: serializedSteps,
      trigger: serializedTrigger,
      conditions,
      projectId: this.projectId,
    };
  }

  /**
   * Apply automation data received from the automation assistant to the current UI state
   */
  private async applyAutomationData(automationData: Partial<Automation>) {
    try {
      // Use the existing JSON loading logic to properly populate the UI
      await this.loadAutomationFromJSON(automationData as Automation);

      // Show success message
      this.msgSrvc.show('Automation has been generated and applied!');
    } catch (error) {
      console.error('Error applying automation data:', error);
      this.msgSrvc.show('Failed to apply generated automation data');
    }
  }

  async sendAssistantMessage() {
    if (!this.assistantInput?.trim() || this.isAssistantThinking) return;

    // Check if feature is enabled
    if (!this.isAutomationAssistantEnabled) {
      this.msgSrvc.showError('Automation assistant feature is not available');
      return;
    }

    // Add user message
    const userMessage: AutomationAssistantMessage = {
      role: 'user',
      content: this.assistantInput.trim(),
    };
    this.assistantMessages.push(userMessage);

    const userInput = this.assistantInput.trim();
    this.assistantInput = '';
    this.isAssistantThinking = true;

    try {
      // Get current automation state using centralized serialization
      const currentAutomationState = this.serializeAutomationState();

      // Call the automation assistant API
      const response = await this.automationAssistantService.sendMessage({
        history: this.assistantMessages,
        currentAutomation: currentAutomationState,
      });

      // Add assistant response(s)
      response.forEach((message) => {
        if (message.role === 'assistant') {
          this.assistantMessages.push(message);

          // If the assistant returned automation data, apply it to the current automation
          if (message.automationData) {
            this.applyAutomationData(message.automationData);
          }
        }
      });

      this.scrollToBottom();
    } catch (error) {
      console.error('Error calling automation assistant:', error);
      // Fallback message
      this.assistantMessages.push({
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      });
      this.msgSrvc.showError('Failed to get response from automation assistant');
      this.scrollToBottom();
    } finally {
      this.isAssistantThinking = false;
    }
  }

  onAssistantEnterKey(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey && !this.isAssistantThinking) {
      event.preventDefault();
      this.sendAssistantMessage();
    }
  }

  // Scroll to bottom when new messages arrive (like AI tester)
  private scrollToBottom() {
    setTimeout(() => {
      const messagesContainer = document.querySelector('.assistant-messages');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 100);
  }

  /**
   * Navigate to the integrations page to browse more apps
   */
  navigateToIntegrations() {
    this.navCtrl.navigateRoot('/dashboard/settings/apps');
  }

  async showOptions(event: Event) {
    const DELETE = 'Delete';

    const options: ActionDropdownType[] = [
      {
        label: DELETE,
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (_index: number, label: string) => {
          popover.dismiss();

          if (label === DELETE) {
            this.delete();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }
}
